import React from 'react'
import {
  AlertTriangle,
  ArrowRight,
  Cable,
  CheckCircle2,
  Circle,
  ClipboardList,
  FileCheck2,
  FolderCog,
  Network,
  Send,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { ProjectDetail } from '@/lib/projectsApi'
import { useTranslation } from 'react-i18next'

interface Props {
  project: ProjectDetail
  systemCount: number
  canEdit: boolean
  onEditSetup: () => void
  onOpenWiring: () => void
  onOpenDocuments: (subtab?: 'risk-assessment' | 'asset-inventory') => void
  onOpenZones: () => void
  onOpenCsdd: () => void
  onOpenTesting: () => void
  onOpenDeliverables: () => void
}

type StageState = 'complete' | 'next' | 'locked'

interface WorkflowStage {
  number: number
  title: string
  detail: string
  state: StageState
}

function StageIcon({ state }: { state: StageState }) {
  if (state === 'complete') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />
  if (state === 'next') return <Circle className="h-4 w-4 fill-blue-500 text-blue-500" />
  return <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600" />
}

export const ComplianceWorkspace: React.FC<Props> = ({
  project,
  systemCount,
  canEdit,
  onEditSetup,
  onOpenWiring,
  onOpenDocuments,
  onOpenZones,
  onOpenCsdd,
  onOpenTesting,
  onOpenDeliverables,
}) => {
  const { t } = useTranslation()
  const profiles = project.compliance_profiles ?? []
  const hasClassSelection = profiles.length >= 1 && profiles.length <= 2
  const hasVerifiedAuthorities = profiles.every(profile => profile.authority_status === 'verified')
  const setupComplete = hasClassSelection && hasVerifiedAuthorities
  const systemsReady = setupComplete && systemCount > 0

  const stages: WorkflowStage[] = [
    {
      number: 1,
      title: t('compliance.stages.setup.title'),
      detail: t('compliance.stages.setup.detail'),
      state: setupComplete ? 'complete' : 'next',
    },
    {
      number: 2,
      title: t('compliance.stages.discovery.title'),
      detail: t('compliance.stages.discovery.detail'),
      state: systemsReady ? 'complete' : setupComplete ? 'next' : 'locked',
    },
    {
      number: 3,
      title: t('compliance.stages.generate.title'),
      detail: t('compliance.stages.generate.detail'),
      state: systemsReady ? 'next' : 'locked',
    },
    {
      number: 4,
      title: t('compliance.stages.zones.title'),
      detail: t('compliance.stages.zones.detail'),
      state: systemsReady ? 'next' : 'locked',
    },
    {
      number: 5,
      title: 'CSDD',
      detail: t('compliance.stages.csdd.detail'),
      state: systemsReady ? 'next' : 'locked',
    },
    {
      number: 6,
      title: t('compliance.stages.testing.title'),
      detail: t('compliance.stages.testing.detail'),
      state: systemsReady ? 'next' : 'locked',
    },
    {
      number: 7,
      title: t('compliance.stages.deliverables.title'),
      detail: t('compliance.stages.deliverables.detail'),
      state: systemsReady ? 'next' : 'locked',
    },
  ]

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <FolderCog className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                {t('compliance.title')}
              </h2>
              <span className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {t('compliance.step', { number: 1 })}
              </span>
            </div>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {t('compliance.description')}
            </p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onEditSetup}>
              {t('compliance.editSetup')}
            </Button>
          )}
        </div>

        <div className="mt-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {profiles.map(profile => (
              <div
                key={profile.profile_code}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/30"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    {profile.class_society}
                  </span>
                  <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {profile.position === 1 ? t('compliance.primary') : t('compliance.secondary')}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-400">{profile.edition_label}</span>
                </div>
                <p className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                  {profile.notation_qualifier || profile.notation_label}
                </p>
                <p className="mt-1 text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                  {profile.rule_reference}
                </p>
                {profile.authority_status === 'template_only' && (
                  <p className="mt-2 flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-3 w-3" />
                    {t('compliance.ruleNeedsConfirmation')}
                  </p>
                )}
              </div>
            ))}
            {profiles.length === 0 && (
              <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 sm:col-span-2 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                {t('compliance.noProfile')}
              </div>
            )}
          </div>
        </div>

        {!setupComplete && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {!hasClassSelection
              ? t('compliance.selectProfileBeforeStep2')
              : t('compliance.confirmAuthorityBeforeStep2')}
          </div>
        )}
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('compliance.vesselWorkflow')}
              </h3>
              <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                {t('compliance.registeredSystems', { count: systemCount })}
              </p>
            </div>
          </div>

          <ol className="mt-3 divide-y divide-slate-100 dark:divide-slate-700/70">
            {stages.map(stage => (
              <li key={stage.number} className="flex gap-3 py-3 first:pt-1 last:pb-1">
                <div className="mt-0.5 flex flex-col items-center gap-1">
                  <StageIcon state={stage.state} />
                  <span className="text-[10px] font-semibold text-slate-400">{stage.number}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className={
                      'text-sm font-medium ' +
                      (stage.state === 'locked'
                        ? 'text-slate-400 dark:text-slate-500'
                        : 'text-slate-800 dark:text-slate-100')
                    }>
                      {stage.title}
                    </p>
                    {stage.state === 'complete' && (
                      <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">{t('compliance.ready')}</span>
                    )}
                    {stage.state === 'next' && (
                      <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                        {stage.number === 1 ? t('compliance.needsAttention') : t('compliance.nextStep')}
                      </span>
                    )}
                    {stage.state === 'locked' && (
                      <span className="text-[10px] text-slate-400">{t('compliance.locked')}</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {stage.detail}
                  </p>
                  {stage.number === 2 && setupComplete && canEdit && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={onOpenWiring}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {t('compliance.openWiring')}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                      <p className="mt-1 text-[10px] text-slate-400">
                        {t('compliance.wiringInstruction')}
                      </p>
                    </div>
                  )}
                  {stage.number === 3 && systemsReady && (
                    <div className="mt-2 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => onOpenDocuments('risk-assessment')}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {t('compliance.generateExclusionRa')}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenDocuments('asset-inventory')}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {t('compliance.generateAssetInventory')}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {stage.number === 4 && systemsReady && (
                    <button
                      type="button"
                      onClick={onOpenZones}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('compliance.openZones')} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                  {stage.number === 5 && systemsReady && (
                    <button
                      type="button"
                      onClick={onOpenCsdd}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('compliance.openCsdd')} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                  {stage.number === 6 && systemsReady && (
                    <button
                      type="button"
                      onClick={onOpenTesting}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('compliance.openTesting')} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                  {stage.number === 7 && systemsReady && (
                    <button
                      type="button"
                      onClick={onOpenDeliverables}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('compliance.openDeliverables')} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        <div className="space-y-4">
          <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
              <Send className="h-4 w-4 text-blue-500" />
              {t('compliance.routing.title')}
            </h3>
            <div className="mt-3 space-y-2 text-xs">
              <RouteLine icon={<FileCheck2 className="h-3.5 w-3.5" />} label={t('compliance.routing.targetSystem')} value={t('compliance.routing.targetSystemValue')} />
              <RouteLine icon={<ClipboardList className="h-3.5 w-3.5" />} label={t('compliance.routing.exclusion')} value={t('compliance.routing.exclusionValue')} />
              <RouteLine icon={<Network className="h-3.5 w-3.5" />} label={t('compliance.routing.conduit')} value={t('compliance.routing.conduitValue')} />
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
              <Cable className="h-4 w-4 text-slate-500" />
              {t('compliance.principles.title')}
            </h3>
            <ul className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              <li>• {t('compliance.principles.ai')}</li>
              <li>• {t('compliance.principles.unknown')}</li>
              <li>• {t('compliance.principles.network')}</li>
              <li>• {t('compliance.principles.rules')}</li>
            </ul>
          </section>
        </div>
      </div>

      {setupComplete && (
        <section className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/60 dark:bg-blue-900/10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('compliance.raDeliverable')}</h3>
              <p className="mt-0.5 text-[11px] text-slate-600 dark:text-slate-300">
                {t('compliance.raDeliverableDescription')}
              </p>
            </div>
            <Button size="sm" onClick={() => onOpenDocuments()}>
              {t('compliance.openDocuments')} <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}

function RouteLine({ icon, label, value }: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-900/40">
      <span className="mt-0.5 text-slate-400">{icon}</span>
      <span className="min-w-0">
        <span className="block font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <span className="text-slate-500 dark:text-slate-400">{value}</span>
      </span>
    </div>
  )
}
